import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function useLocalDebounced(initialValue, onCommit, delay) {
  const [value, setValue] = useState(initialValue ?? "");
  const timerRef = useRef(null);
  const composingRef = useRef(false);
  const valueRef = useRef(value);
  const onCommitRef = useRef(onCommit);
  valueRef.current = value;
  onCommitRef.current = onCommit;

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      onCommitRef.current(valueRef.current);
    }
  }, []);

  const scheduleCommit = (v) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onCommit(v), delay);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    if (!composingRef.current) scheduleCommit(v);
  };
  const handleCompositionStart = () => {
    composingRef.current = true;
    clearTimeout(timerRef.current);
  };
  const handleCompositionEnd = (e) => {
    composingRef.current = false;
    scheduleCommit(e.target.value);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.stopPropagation();
  };
  const handleBlur = () => {
    clearTimeout(timerRef.current);
    onCommit(value);
  };
  return { value, handleChange, handleBlur, handleCompositionStart, handleCompositionEnd, handleKeyDown };
}

export function DebouncedInput({ initialValue, onCommit, delay = 450, ...props }) {
  const { value, handleChange, handleBlur, handleCompositionStart, handleCompositionEnd, handleKeyDown } = useLocalDebounced(initialValue, onCommit, delay);
  return (
    <Input
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}

export function DebouncedTextarea({ initialValue, onCommit, delay = 450, ...props }) {
  const { value, handleChange, handleBlur, handleCompositionStart, handleCompositionEnd, handleKeyDown } = useLocalDebounced(initialValue, onCommit, delay);
  return (
    <Textarea
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}
